<i18n locale="en">
{
  "labels": {
    "empty": "No options found"
  }
}
</i18n>

<template>
  <!-- Modal rendering on small screens -->
  <Teleport v-if="shouldRenderAsModal && props.open" to="body">
    <div class="own-dropdown-menu-v2__modal-backdrop" @click="onBackdropClick">
      <OwnCard
        class="own-dropdown-menu-v2__modal-container"
        control
        @click.stop
      >
        <div
          v-if="props.items.length > 0"
          class="own-dropdown-menu-v2__content flex-col"
        >
          <div
            v-for="item of props.items"
            :key="item.value"
            class="own-dropdown-menu-v2__item"
            :class="{
              'text-accent bg-surface-active': props.active === item.value,
            }"
            :data-testid="item.testId"
            @click="onItemClick(item)"
          >
            <slot name="left" :item="item">
              <Component :is="item.icon" v-if="item.icon" size="24" />
            </slot>

            <slot name="label" :item="item">
              <OwnType
                class="text--truncate"
                el="span"
                :text="item.label"
                variant="title-regular"
                color="inherit"
                :title="item.label"
              />
            </slot>

            <slot name="right" :item="item" />
          </div>
        </div>

        <OwnType
          v-else
          class="own-dropdown-menu-v2__empty"
          :text="t('labels.empty')"
          variant="title-regular"
        />

        <slot name="actions" />
      </OwnCard>
    </div>
  </Teleport>

  <!-- Regular dropdown rendering on larger screens -->
  <DropdownMenuContent
    v-if="!shouldRenderAsModal"
    class="own-dropdown-menu-v2"
    :side="side"
    :side-offset="sideOffset"
    :align="align"
  >
    <OwnCard
      class="own-dropdown-menu-v2__container"
      :class="{
        'own-dropdown-menu-v2__container--match-trigger-width':
          props.matchTriggerWidth,
      }"
      control
    >
      <div
        v-if="props.items.length > 0"
        class="own-dropdown-menu-v2__content flex-col"
      >
        <DropdownMenuItem
          v-for="item of props.items"
          :key="item.value"
          as-child
          @select="onItemClick(item)"
        >
          <div
            class="own-dropdown-menu-v2__item"
            :class="{
              'text-accent bg-surface-active': props.active === item.value,
            }"
            :data-testid="item.testId"
          >
            <slot name="left" :item="item">
              <Component :is="item.icon" v-if="item.icon" size="24" />
            </slot>

            <slot name="label" :item="item">
              <OwnType
                class="text--truncate"
                el="span"
                :text="item.label"
                variant="title-regular"
                color="inherit"
                :title="item.label"
              />
            </slot>

            <slot name="right" :item="item" />
          </div>
        </DropdownMenuItem>
      </div>

      <OwnType
        v-else
        class="own-dropdown-menu-v2__empty"
        :text="t('labels.empty')"
        variant="title-regular"
      />

      <slot name="actions" />
    </OwnCard>
  </DropdownMenuContent>
</template>

<script lang="ts" setup generic="T extends object">
import { DropdownMenuContent, DropdownMenuItem } from 'reka-ui'
import { computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBreakpoints } from '@/utils/useBreakpoints'

import { OwnCard } from '../../OwnCard'
import { OwnType } from '../../OwnType'
import { OwnDropdownItem } from '../types'

const LINE_HEIGHT = 53
const PADDING_HEIGHT = 60

const props = withDefaults(
  defineProps<{
    active?: string
    align?: 'start' | 'center' | 'end'
    items: (OwnDropdownItem & T)[]
    matchTriggerWidth?: boolean
    modalOnMobile?: boolean
    open?: boolean
    side?: 'top' | 'right' | 'bottom' | 'left'
    sideOffset?: number
    visibleOptions?: number
  }>(),
  {
    active: undefined,
    align: 'start',
    matchTriggerWidth: false,
    modalOnMobile: false,
    open: false,
    side: 'bottom',
    sideOffset: 8,
    visibleOptions: 4,
  }
)

type OwnDropdownMenuEmits = {
  (event: 'selected', value: OwnDropdownItem): void
  (event: 'close'): void
}

const emit = defineEmits<OwnDropdownMenuEmits>()

const { t } = useI18n()
const { isDesktop } = useBreakpoints()

// Check if we should render as modal (on mobile when prop is enabled)
const shouldRenderAsModal = computed(
  () => props.modalOnMobile && !isDesktop.value
)
const isMobileModalOpen = computed(
  () => shouldRenderAsModal.value && props.open
)

const maxContentHeight = computed(() => {
  return `${LINE_HEIGHT * props.visibleOptions + PADDING_HEIGHT}px`
})

const onItemClick = (item: OwnDropdownItem) => {
  emit('selected', item)
}

const onBackdropClick = () => {
  emit('close')
}

const onGlobalKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

watch(
  isMobileModalOpen,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener('keydown', onGlobalKeydown)
    } else {
      window.removeEventListener('keydown', onGlobalKeydown)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
})
</script>

<style lang="scss" scoped>
.own-dropdown-menu-v2 {
  &__container {
    border-radius: 12px;
    overflow: hidden;
    max-height: v-bind(maxContentHeight);

    &--match-trigger-width {
      width: var(--reka-dropdown-menu-trigger-width);
    }
  }

  &__modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: var(--color-surface-modal);
    z-index: 300; /* Dialogs range: 300-399 */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  &__modal-container {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    max-height: 70vh;
    width: 100%;
    max-width: 450px;
    z-index: 301; /* Above backdrop */
    pointer-events: auto;
    display: flex;
    flex-direction: column;
  }

  &__content {
    overflow-y: auto;
    padding: 2px;
  }

  &__item {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    width: 100%;

    transition: background-color 100ms ease-in;

    &:hover {
      background-color: var(--background-highlight);
    }
  }

  &__empty {
    padding: 8px;
  }
}
</style>
